import { Suspense, useEffect } from 'react';

import { Outlet } from 'react-router-dom';

import { useUserback } from '@userback/react';

import { BuyMinutesModal } from '@/features/pricing/buyMinutes';

import { EnterprisePaymentRequiredModal, UpgradePlanModal, useProducts } from '@/entities/pricing';
import { useBalanceStore } from '@/entities/user/balance';
import { useCognitoCheckUser } from '@/entities/user/settings';

import { useAppSelector } from '@/shared/hooks-tmp/reduxToolkitHooks';
import { useSubscription } from '@/shared/hooks-tmp/useSubscription';
import { getABTest, isABTestEnabled } from '@/shared/lib/abTest';
import { PlanCategory } from '@/shared/types/Billing';

import { useHandlePaymentSuccess } from './hooks/useHandlePaymentSuccess';

export const BaseLayout = () => {
  const { identify } = useUserback();

  const user = useAppSelector((state) => state.user);
  const { data } = useCognitoCheckUser();

  const { hasEnterprisePlan, hasTrialPlan } = useBalanceStore();

  const nameCognitoUser = data?.name;
  const cognitoId = data?.cognitoId;
  const subscription = useSubscription(user);
  const planName = subscription?.planName;

  const { products } = useProducts();
  const businessPlans = products?.plan?.filter((planData) => planData.category === PlanCategory.Business);
  const isBusinessActive = businessPlans?.find((planData) => planData.id === user?.subscription?.plan?.product_id);
  const forceAlignmentABTests = getABTest({
    testName: ['forced_alignment', 'test_forced_alignment'],
    tests: user?.feature_groups,
  });
  const isForceAlignment = isABTestEnabled(forceAlignmentABTests);

  useEffect(() => {
    if (user) {
      identify(user.id, {
        name: nameCognitoUser || user.email,
        account_id: cognitoId,
        email: user.email,
        is_business: isBusinessActive ? 'Yes' : 'No',
        is_enterprise: hasEnterprisePlan ? 'Yes' : 'No',
        is_fa: isForceAlignment ? 'Yes' : 'No',
        is_trial: hasTrialPlan ? 'Yes' : 'No',
        plan: planName,
      });
    }
  }, [
    cognitoId,
    hasEnterprisePlan,
    hasTrialPlan,
    identify,
    isBusinessActive,
    isForceAlignment,
    nameCognitoUser,
    planName,
    user,
  ]);

  useHandlePaymentSuccess(user);

  return (
    <Suspense>
      <Outlet />
      <UpgradePlanModal />
      <EnterprisePaymentRequiredModal />
      <BuyMinutesModal />
    </Suspense>
  );
};
